import {
  useCallback, useContext, useMemo, useState,
} from 'react';
import { LogicaContext } from '../../../providers/authLogicaProvider';
import { AppContext } from '../../../providers/authProvider';
import api from '../../../api/req';
import { checkTaskPromise } from '../../../api/checktask';

const BACKEND = '/api/logica/dps/';

const useSTAFiles = () => {
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const {
    ticket,
    setTicket,
  } = useContext(LogicaContext);
  const { auth, currentUser } = useContext(AppContext);
  const [org, setOrg] = useState(currentUser.org);
  const addParams = useMemo(
    () => (currentUser.is_superuser && org ? { org: org.id } : {}),
    [currentUser.is_superuser, org],
  );

  const [dates, setDates] = useState([]);
  const [bCode, setBCode] = useState('');
  const [year, setYear] = useState(() => {
    const d = new Date();
    return d.getFullYear();
  });

  const loadDates = useCallback(
    async () => {
      const r = await api.get$(`${BACKEND}`, auth, {
        ticket,
        year,
        ...addParams,
      });
      if (r.ok) {
        return r.json();
      }
      if (r.status === 401) setTicket(null);
      throw new Error(`${r.status} ${r.statusText}`);
    },
    [addParams, auth, setTicket, ticket, year],
  );
  const onLoadDates = useCallback(() => {
    setLoading(true);
    setErr(null);
    setDone(false);
    loadDates()
      .then((d) => {
        setBCode(d.budget_code);
        setDates(d.result.map(({ datefile, uploaded }) => ({
          datefile,
          label: new Date(datefile),
          checked: !uploaded,
        })).sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        }));
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [loadDates]);

  const onCheckDate = useCallback(
    (date, value) => setDates(
      (o) => o.map((d) => (d.datefile === date ? { ...d, checked: value } : d)),
    ),
    [],
  );

  const loadData = useCallback(
    async (_bcode, _dates) => {
      const r = await api.post(BACKEND, auth, {
        dates: _dates, budget_code: _bcode, ticket, ...addParams,
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      // eslint-disable-next-line camelcase
      const { task_id } = await r.json();
      await checkTaskPromise(task_id, auth);
      setDone(true);
    },
    [addParams, auth, ticket],
  );

  const onLoadWithParams = useCallback(
    () => {
      setLoading(true);
      setErr(null);
      loadData(bCode, dates.filter((d) => d.checked).map((d) => d.datefile))
        .then(() => null)
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [bCode, dates, loadData],
  );

  const onQuickLoad = useCallback(
    () => {
      const loader = async () => {
        const dds = await loadDates();
        const result = await loadData(
          dds.budget_code,
          dds.result.filter((dd) => !dd.uploaded).map((dd) => dd.datefile),
        );
        return result;
      };
      setLoading(true);
      setErr(null);
      setDone(false);
      loader()
        .then(() => setDone(true))
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [loadData, loadDates],
  );
  return {
    ticket,
    loading,
    done,
    onLoadDates,
    err,
    year,
    setYear,
    dates,
    onCheckDate,
    onLoadWithParams,
    onQuickLoad,
    canOrg: currentUser.is_superuser,
    org,
    setOrg,
  };
};

export default useSTAFiles;
